/**
 * Keeps the quantity selector in sync with the selected variant.
 *
 * The quantity selector sits next to <buy-buttons> instead of inside it, so the partial re-render on variant
 * change never replaces it and the server rendered markup would stay on the initial variant. Quantity rules
 * and visibility are patched in place instead, which also preserves a quantity the customer already typed.
 */
class QuantityVariantRules extends HTMLElement {
	connectedCallback() {
		this.input = this.querySelector('.quantity-selector__input');
		this.form = document.forms[this.getAttribute('form')];

		if (!this.input || !this.form) {
			return;
		}

		this.onVariantChange = this.handleVariantChange.bind(this);
		this.form.addEventListener('variant:change', this.onVariantChange);
	}

	disconnectedCallback() {
		if (this.form && this.onVariantChange) {
			this.form.removeEventListener('variant:change', this.onVariantChange);
		}
	}

	handleVariantChange(event) {
		const variant = event.detail.variant;

		this.hidden = !variant?.available;

		if (!variant?.quantity_rule) {
			return;
		}

		this.applyRule(variant.quantity_rule);
	}

	applyRule(rule) {
		const min = rule.min || 1;
		const increment = rule.increment || 1;

		this.input.min = min;
		this.input.step = increment;

		if (rule.max) {
			this.input.max = rule.max;
		} else {
			this.input.removeAttribute('max');
		}

		this.setQuantity(this.closestValidQuantity(min, increment, rule.max));
	}

	closestValidQuantity(min, increment, max) {
		const requested = parseInt(this.input.value, 10) || min;
		const bounded = max ? Math.min(Math.max(requested, min), max) : Math.max(requested, min);
		const steps = Math.floor((bounded - min) / increment);

		return min + steps * increment;
	}

	setQuantity(quantity) {
		this.input.value = quantity.toString();
		this.input.style.setProperty('--quantity-input-characters-count', `${this.input.value.length}ch`);
	}
}

if (!window.customElements.get('quantity-variant-rules')) {
	window.customElements.define('quantity-variant-rules', QuantityVariantRules);
}
